import { useState, useEffect, useContext } from 'react';
import { StateContext } from '../../../../Controllers/context/state';
import supabase from '../../../../Controllers/utils/supabaseClient';
import { fetchIntradayChartData, fetchDailyChartData } from '../components/StockChart/support';

export const useStockData = (symbol, chartType, selectedDate) => {
    const { setSectorSymbol } = useContext(StateContext);
    const [loading, setLoading] = useState(true);
    const [tickerData, setTickerData] = useState({});
    const [historicalData, setHistoricalData] = useState([]);

    const fetchTickerInfo = async () => {
        const res = await supabase.from('asset_symbols').select('*').eq('symbol', symbol);
        if (res?.error) {
            const error = new Error(res?.error?.message);
            console.error(error?.message, error);
            return;
        }
        if (res?.data?.length) {
            const { name, type, gsector, gsubind } = res.data[0];
            setTickerData({ name, type, gsector, gsubind });
        }
    };

    const fetchSectorETFSymbol = async () => {
        const res = await supabase.from('asset_symbols').select('*').eq('symbol', symbol);
        const sector = res?.data?.[0]?.gsector;

        if (!sector) {
            setSectorSymbol(null);
            return;
        }

        const etfRes = await supabase.from('entity_ui_sets')
            .select('data')
            .eq('name', 'marketreaderetfmonitorv2');

        const { equity_sectors } = etfRes.data?.[0]?.data?.entities;

        const sectorETFSymbol = equity_sectors.find(etf =>
            sector.includes(etf.name)
        );

        if (!sectorETFSymbol) {
            setSectorSymbol(null);
            return;
        }
        setSectorSymbol(sectorETFSymbol.symbol);
    };

    // Fetch daily historical data
    useEffect(() => {
        let cancel = false;
        if (chartType === '10m') return;
        setLoading(true);

        const fetchHistoricalData = async () => {
            try {
                await fetchTickerInfo();
                await fetchSectorETFSymbol();

                const sixMonthsAgo = new Date();
                sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                const sixMonthsAgoEpoch = Math.floor(sixMonthsAgo.getTime() / 1000);

                const recentData = await fetchDailyChartData({
                    ticker: symbol,
                    epochStart: sixMonthsAgoEpoch,
                    epochEnd: null,
                    retry: true
                });

                setHistoricalData(recentData);
                setLoading(false);

                const twelveMonthsAgo = new Date();
                twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
                const twelveMonthsAgoEpoch = Math.floor(twelveMonthsAgo.getTime() / 1000);

                const jan2023 = new Date('2023-01-01T00:00:00Z');
                const jan2023Epoch = Math.floor(jan2023.getTime() / 1000);

                const [midData, oldestData] = await Promise.all([
                    fetchDailyChartData({
                        ticker: symbol,
                        epochStart: twelveMonthsAgoEpoch,
                        epochEnd: sixMonthsAgoEpoch,
                        retry: true
                    }),
                    fetchDailyChartData({
                        ticker: symbol,
                        epochStart: jan2023Epoch,
                        epochEnd: twelveMonthsAgoEpoch,
                        retry: true
                    })
                ]);

                if (cancel) return;

                const allTimestamps = new Set(recentData.map(item => item.time_utc));
                const combinedData = [...recentData];

                [midData, oldestData].forEach(dataset => {
                    dataset.forEach(item => {
                        if (!allTimestamps.has(item.time_utc)) {
                            allTimestamps.add(item.time_utc);
                            combinedData.push(item);
                        }
                    });
                });

                const sortedData = combinedData.sort((a, b) => a.time_utc - b.time_utc);
                setHistoricalData(sortedData);
            } catch (error) {
                console.error("Error fetching historical data:", error);
                setLoading(false);
            }
        };

        fetchHistoricalData();

        return () => {
            cancel = true;
        };
    }, [symbol, chartType]);

    // Fetch intraday data
    useEffect(() => {
        if (chartType === 'D') return;
        setLoading(true);

        const fetchIntradayData = async () => {
            let epochEnd = null;
            let epochStart;
            await fetchTickerInfo();
            await fetchSectorETFSymbol();

            if (selectedDate) {
                epochEnd = selectedDate;
                epochStart = epochEnd - (5 * 24 * 60 * 60);
            } else {
                epochStart = Math.floor(new Date().getTime() / 1000) - (5 * 24 * 60 * 60);
            }

            const data = await fetchIntradayChartData({
                ticker: symbol,
                epochStart: epochStart,
                epochEnd: epochEnd,
            });
            setHistoricalData(data);
            setLoading(false);
        };

        fetchIntradayData();
    }, [symbol, chartType, selectedDate]);

    return {
        loading,
        tickerData,
        historicalData
    };
};


